/** Deterministic transfer trajectory: metres, seconds, gravity, then chute contact.
 * Scroll scrubs time in both directions; it never accumulates simulation drift. */
export const ROCK_COUNT = 60;
export const DURATION = 10.5;
export const CHAPTERS = [0, .19, .46, .8] as const;
export const CHUTE_SLOPE = .3;
export const CHUTE_START = .15;
export const CHUTE_END = 4.65;
export const chuteHeight = (x: number) => 1.8 - CHUTE_SLOPE * (x - CHUTE_START);
export const clamp = (value: number, low = 0, high = 1) => Math.min(high, Math.max(low, value));
export const smooth = (value: number) => { const t = clamp(value); return t * t * (3 - 2 * t); };
export function random(seed: number) {
  const x = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
}
export function rockSpec(index: number) {
  return { birth: -1.15 + index * .11, radius: .12 + random(index + 2) * .16, z: (random(index + 51) - .5) * 1.9, speed: 1.75 + random(index + 13) * .38 };
}
export function sampleRock(index: number, progress: number) {
  const spec = rockSpec(index), age = clamp(progress) * DURATION - spec.birth;
  const beltTime = 1.75, releaseX = -.28, releaseY = 3.55 + spec.radius;
  // Solve the intersection of a ballistic trajectory and the sloping liner.
  const clearance = releaseY - chuteHeight(releaseX) - spec.radius;
  const b = CHUTE_SLOPE * spec.speed;
  const fallTime = (b + Math.sqrt(b * b + 2 * 9.81 * clearance)) / 9.81;
  const impactAge = beltTime + fallTime, impactX = releaseX + spec.speed * fallTime;
  const contactAge = age - impactAge;
  let x = releaseX, y = releaseY, phase: "hidden" | "belt" | "fall" | "impact" | "slide" | "exit" = "hidden";
  if (age >= 0 && age < beltTime) {
    x = -4.8 + (releaseX + 4.8) * age / beltTime; phase = "belt";
  } else if (age >= beltTime && contactAge < 0) {
    const t = age - beltTime; x = releaseX + spec.speed * t; y = releaseY - .5 * 9.81 * t * t; phase = "fall";
  } else if (contactAge >= 0) {
    x = impactX + spec.speed * .68 * contactAge + .5 * 1.8 * contactAge * contactAge;
    // One small rebound, always above the plate; larger rocks settle earlier.
    const bounce = .28;
    y = chuteHeight(x) + spec.radius + (contactAge < bounce ? .16 * Math.sin(Math.PI * contactAge / bounce) : 0);
    phase = contactAge < bounce ? "impact" : "slide";
    if (x > CHUTE_END) {
      phase = "exit";
      const exitAge = (-spec.speed * .68 + Math.sqrt((spec.speed * .68) ** 2 + 3.6 * (CHUTE_END - impactX))) / 1.8;
      const t = contactAge - exitAge;
      y = Math.max(.115 + spec.radius, chuteHeight(CHUTE_END) + spec.radius - .9 * t - 4.905 * t * t);
    }
  }
  const visible = age >= 0 && y > -.25 && x < 8.65;
  const rotation = phase === "belt" ? 0 : Math.max(0, age - beltTime) * (phase === "fall" ? 1.5 : 2.7);
  return { ...spec, x, y, phase, visible, rotation, contactAge, impactX, impactTime: spec.birth + impactAge };
}
